<template>
  <div class="device-showcase-container">
    <!-- Carousel Mode -->
    <div v-if="mode === 'carousel'" class="carousel-container">
      <div 
        class="carousel-wrapper" 
        @mousedown="startDrag" 
        @mousemove="onDrag" 
        @mouseup="stopDrag"
        @mouseenter.passive="stopAutoRotate" 
        @mouseleave.passive="startAutoRotate" 
        style="cursor: grab;"
        ref="carouselRef"
  >
        <div 
          v-for="(device, index) in devices" 
          :key="index" 
          class="carousel-slide" 
          :style="getSlideStyle(index)"
          @click="() => handleDeviceClick(device, index)"
        >
          <DeviceMockup
            :device-type="device.type"
            :wallpaper="getWallpaperForDevice(device.type)"
            :wallpaper-id="getWallpaperIdForDevice(device.type) || getCurrentWallpaperId()"
            :name="device.name"
            :show-label="showLabels"
    :clickable="true"
    fit-option="auto"
    :resolution="getResolutionForDevice(device.type)"
    :scale="getDeviceScale(device.type)"
          />
        </div>
      </div>
    </div>

    <!-- Grid Mode -->
    <div v-else-if="mode === 'grid'" class="grid-container">
      <BaseGrid :cols="gridCols" :sm-cols="gridSmCols" :md-cols="gridMdCols" :lg-cols="gridLgCols" :gap="gridGap">
        <div 
          v-for="(device, index) in devices" 
          :key="index"
          class="grid-item"
          @click="() => handleDeviceClick(device, index)"
        >
          <DeviceMockup
            :device-type="device.type"
            :wallpaper="getDeviceWallpaper(index)"
            :wallpaper-id="getDeviceWallpaperId(index)"
            :name="device.name"
            :show-label="showLabels"
            :clickable="true"
          />
        </div>
      </BaseGrid>
    </div>

    <!-- Single Mode -->
    <div v-else-if="mode === 'single'" class="single-container">
      <DeviceMockup
        :device-type="selectedDevice.type"
        :wallpaper="currentWallpaper"
        :wallpaper-id="getCurrentWallpaperId()"
        :name="selectedDevice.name"
        :show-label="showLabels"
        :clickable="clickable"
        @click="() => handleDeviceClick(selectedDevice, 0)"
      />
    </div>

    <!-- Navigation Controls (for carousel mode) -->
    <div v-if="mode === 'carousel' && showControls" class="carousel-controls">
      <BaseButton 
        variant="ghost" 
        size="sm" 
        @click="prevSlide"
        class="control-button"
      >
        ←
      </BaseButton>
      <BaseButton 
        variant="ghost" 
        size="sm" 
        @click="nextSlide"
        class="control-button"
      >
        →
      </BaseButton>
    </div>

    <!-- Device Selector (optional) -->
    <div v-if="showDeviceSelector && mode !== 'carousel'" class="device-selector">
      <BaseButton
        v-for="(device, index) in devices"
        :key="index"
        :variant="selectedDeviceIndex === index ? 'primary' : 'outline'"
        size="sm"
        @click="selectDevice(index)"
        class="selector-button"
      >
        {{ device.name }}
        <span v-if="getDeviceVariationCount(device.type) > 1" class="variation-indicator">
          {{ getCurrentVariationIndex(device.type) + 1 }}/{{ getDeviceVariationCount(device.type) }}
        </span>
      </BaseButton>
    </div>

    <!-- Variation Controls (for devices with multiple images) -->
    <div v-if="mode === 'single' && getDeviceVariationCount(selectedDevice.type) > 1" class="variation-controls">
      <BaseButton 
        variant="ghost" 
        size="sm" 
        @click="prevVariation(selectedDevice.type)"
        class="control-button"
      >
        ← Prev
      </BaseButton>
      <span class="variation-counter">
        {{ getCurrentVariationIndex(selectedDevice.type) + 1 }} / {{ getDeviceVariationCount(selectedDevice.type) }}
      </span>
      <BaseButton 
        variant="ghost" 
        size="sm" 
        @click="nextVariation(selectedDevice.type)"
        class="control-button"
      >
        Next →
      </BaseButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch, defineAsyncComponent } from 'vue'
const DeviceMockup = defineAsyncComponent(() => import('./DeviceMockup.vue'))
const BaseGrid = defineAsyncComponent(() => import('./BaseGrid.vue'))
const BaseButton = defineAsyncComponent(() => import('./BaseButton.vue'))
import { wallpaperService } from '../../firebase/services/wallpaperService'
import { getDeviceTypeFromString, warmAspectCache, scoreWallpaperForDevice, isWallpaperLabeledForDevice } from '../../services/previewImageSelector'
import { onSnapshot, query, orderBy, limit } from 'firebase/firestore'
import { wallpapersCollection } from '../../firebase/schema'
import type { Wallpaper } from '../../firebase/schema'

interface Device {
  type: 'phone' | 'iphone' | 'iphone-pro' | 'android' | 'tablet' | 'laptop' | 'desktop' | 'ultrawide' | 'tv' | 'watch'
  name: string
  frameClass?: string
}

interface Props {
  devices: Device[]
  wallpapers?: string[]
  mode?: 'carousel' | 'grid' | 'single'
  autoRotate?: boolean
  rotateInterval?: number
  wallpaperRotateInterval?: number
  showLabels?: boolean
  showControls?: boolean
  showDeviceSelector?: boolean
  clickable?: boolean
  selectedDeviceIndex?: number
  // Grid specific props
  gridCols?: 1 | 2 | 3 | 4 | 5 | 6
  gridSmCols?: 1 | 2 | 3 | 4 | 5 | 6
  gridMdCols?: 1 | 2 | 3 | 4 | 5 | 6
  gridLgCols?: 1 | 2 | 3 | 4 | 5 | 6
  gridGap?: 'sm' | 'md' | 'lg' | 'xl'
}

interface Emits {
  (e: 'device-click', device: Device, index: number): void
  (e: 'device-change', index: number): void
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'carousel',
  autoRotate: true,
  rotateInterval: 3000,
  wallpaperRotateInterval: 3000, // Faster rotation to see all images
  showLabels: true,
  showControls: false,
  showDeviceSelector: false,
  clickable: true,
  selectedDeviceIndex: 0,
  gridCols: 3,
  gridSmCols: 2,
  gridMdCols: 3,
  gridLgCols: 4,
  gridGap: 'md'
})

const emit = defineEmits<Emits>()

// Reactive state
const currentIndex = ref(0)
const currentWallpaperIndex = ref(0)
// Tracks rotation step per device for cycling through top ranked wallpapers
const deviceRotationMap = ref(new Map<string, number>())
// Tracks rotation within device variations (for packs with multiple images per device)
const deviceVariationRotationMap = ref(new Map<string, number>())
const isDragging = ref(false)
const startX = ref(0)
const dragX = ref(0)
const carouselRef = ref<HTMLElement | null>(null)
const dbWallpapers = ref<Wallpaper[]>([])
const loading = ref(true)
  // touch state
  const touchStartX = ref(0)
  const touchDeltaX = ref(0)

// Intervals
let autoRotateInterval: number | null = null
let wallpaperInterval: number | null = null
let unsubscribeWallpapers: (() => void) | null = null

// Computed properties
const selectedDevice = computed(() => props.devices[props.selectedDeviceIndex] || props.devices[0])

const currentWallpaper = computed(() => {
  const wallpapers = dbWallpapers.value.length > 0 ? dbWallpapers.value : (props.wallpapers as unknown as Wallpaper[] || [])
  if (wallpapers.length === 0) return ''
  
  const wallpaper = wallpapers[currentWallpaperIndex.value % wallpapers.length]
  return (wallpaper as any)?.imageUrl || (wallpaper as unknown as string)
})


// Per-device filtered list prioritizing latest images with strict device compatibility
const deviceRankedMap = computed(() => {
  const map = new Map<string, { w: Wallpaper; s: number; date: Date }[]>()
  const wallpapers = dbWallpapers.value.length > 0 ? dbWallpapers.value : (props.wallpapers as unknown as Wallpaper[] || [])
  if (wallpapers.length === 0) return map
  
  props.devices.forEach(d => {
    const device = getDeviceTypeFromString(d.type as unknown as string)
    
    // First filter: only wallpapers that are properly labeled for this device
    const compatibleWallpapers = wallpapers.filter(w => isWallpaperLabeledForDevice(w, device))
    
    const filtered = compatibleWallpapers
      .map(w => ({ 
        w, 
        s: scoreWallpaperForDevice(device, w),
        date: (w as any).createdAt || (w as any).updatedAt || new Date(0)
      }))
      .filter(x => x.s > 0)
      // Sort by score first (best match), then by date as tiebreaker
      .sort((a, b) => {
        if (b.s !== a.s) return b.s - a.s // Higher score first
        const dateA = new Date(a.date).getTime()
        const dateB = new Date(b.date).getTime()
        return dateB - dateA // Newer first as tiebreaker
      })
    
    map.set(d.type, filtered)
  })
  return map
})    

const getSelectedWallpaperForDevice = (deviceType: Device['type']): Wallpaper | null => {
  const filtered = deviceRankedMap.value.get(deviceType) || []
  if (filtered.length === 0) return null
  // Cycle through ALL filtered images, not just a limited pool
  const rotationIndex = deviceRotationMap.value.get(deviceType) || 0
  return filtered[rotationIndex % filtered.length].w
}

const getWallpaperForDevice = (deviceType: Device['type']) => {
  const wp = getSelectedWallpaperForDevice(deviceType)
  if (!wp) return ''

  const device = getDeviceTypeFromString(deviceType as unknown as string)
  
  // STRICT FILTERING: Only show wallpapers that are properly labeled for this device
  if (!isWallpaperLabeledForDevice(wp, device)) {
    return ''
  }

  // Priority 1: Use device-specific image from deviceVariations
  const dv: any = (wp as any).deviceVariations
  if (dv && dv[device] && dv[device].imageUrl) {
    return dv[device].imageUrl
  }

  // Priority 2: Use device-specific image from deviceVariationsList (with multiple image support)
  const dvl: any[] = (wp as any).deviceVariationsList
  if (Array.isArray(dvl) && dvl.length > 0) {
    const entry = dvl.find(v => v.device === device && Array.isArray(v.variations) && v.variations.length > 0)
    if (entry && entry.variations.length > 0) {
      // Handle multiple images per device - cycle through them
      const variationKey = `${(wp as any).id}-${device}`
      const variationRotationIndex = deviceVariationRotationMap.value.get(variationKey) || 0
      const selectedVariation = entry.variations[variationRotationIndex % entry.variations.length]
      
      if (selectedVariation?.imageUrl) {
        return selectedVariation.imageUrl
      }
    }
  }

  // Priority 3: Use main image only if it's appropriate for this device type
  if (wp.imageUrl) {
    const wpAny = wp as any
    
    // Additional safety check: don't show mobile-primary images on desktop devices
    if (wpAny.primaryDevice === 'mobile_phone' && (device === 'laptop' || device === 'desktop' || device === 'ultrawide')) {
      return ''
    }
    
    // Don't show desktop-primary images on mobile devices
    if (wpAny.primaryDevice === 'desktop' && (device === 'mobile_phone' || device === 'iphone' || device === 'android')) {
      return ''
    }
    
    return wp.imageUrl
  }

  // No suitable image found
  return ''
}

const getResolutionForDevice = (deviceType: Device['type']) => {
  const wp = getSelectedWallpaperForDevice(deviceType)
  if (!wp) return undefined
  const device = getDeviceTypeFromString(deviceType as unknown as string)
  
  // Priority 1: Check deviceVariations
  const dv: any = (wp as any).deviceVariations
  if (dv && dv[device] && dv[device].dimensions) {
    const { width, height } = dv[device].dimensions
    if (width && height) return `${width}x${height}`
  }
  
  // Priority 2: Check deviceVariationsList with multiple image support
  const dvl: any[] = (wp as any).deviceVariationsList
  if (Array.isArray(dvl) && dvl.length > 0) {
    const entry = dvl.find(v => v.device === device && Array.isArray(v.variations) && v.variations.length > 0)
    if (entry && entry.variations.length > 0) {
      // Get resolution from the currently selected variation
      const variationKey = `${(wp as any).id}-${device}`
      const variationRotationIndex = deviceVariationRotationMap.value.get(variationKey) || 0
      const selectedVariation = entry.variations[variationRotationIndex % entry.variations.length]
      
      if (selectedVariation?.dimensions?.width && selectedVariation?.dimensions?.height) {
        return `${selectedVariation.dimensions.width}x${selectedVariation.dimensions.height}`
      }
    }
  }
  
  // Priority 3: Fallback to main resolutions
  const r = (wp as any)?.resolutions?.[0]
  return r ? `${r.width}x${r.height}` : undefined
}

const getWallpaperIdForDevice = (deviceType: Device['type']) => {
  const wp = getSelectedWallpaperForDevice(deviceType)
  return (wp as any)?.id
}

// Methods
const fetchWallpapers = async () => {
  try {
    loading.value = true
  const result = await wallpaperService.getWallpapers({ limitCount: 120 })
    dbWallpapers.value = result.wallpapers
    // Warm aspect cache in background; when done, force a tick so computed sorts update
    warmAspectCache(dbWallpapers.value).then(() => {
      // Touch rotation map to trigger recompute without changing UI state
      deviceRotationMap.value = new Map(deviceRotationMap.value)
    })
  } catch (error) {
    console.error('Error fetching wallpapers:', error)
    dbWallpapers.value = []
  } finally {
    loading.value = false
  }
}

const getDeviceWallpaper = (index: number) => {
  // In grid mode, each device should show its own appropriate wallpaper
  const device = props.devices[index]
  if (!device) return ''
  
  return getWallpaperForDevice(device.type)
}

const getCurrentWallpaperId = () => {
  const wallpapers = dbWallpapers.value.length > 0 ? dbWallpapers.value : []
  if (wallpapers.length === 0) return undefined
  
  const wallpaper = wallpapers[currentWallpaperIndex.value % wallpapers.length]
  return typeof wallpaper === 'object' ? wallpaper.id : undefined
}

const getDeviceWallpaperId = (index: number) => {
  // In grid mode, get the wallpaper ID for the specific device
  const device = props.devices[index]
  if (!device) return undefined
  
  return getWallpaperIdForDevice(device.type)
}

// Carousel specific methods
const startDrag = (e: MouseEvent) => {
  if (props.mode !== 'carousel') return
  isDragging.value = true
  startX.value = e.pageX - (carouselRef.value?.offsetLeft || 0)
  if (carouselRef.value) {
    carouselRef.value.style.cursor = 'grabbing'
  }
}

const onDrag = (e: MouseEvent) => {
  if (!isDragging.value || props.mode !== 'carousel') return
  e.preventDefault()
  const x = e.pageX - (carouselRef.value?.offsetLeft || 0)
  const walk = (x - startX.value) * 0.01
  dragX.value = walk
}

const stopDrag = () => {
  if (!isDragging.value || props.mode !== 'carousel') return
  isDragging.value = false
  if (carouselRef.value) {
    carouselRef.value.style.cursor = 'grab'
  }

  if (dragX.value > 10) prevSlide()
  else if (dragX.value < -10) nextSlide()

  dragX.value = 0
}

// Touch handlers for mobile swipe
const onTouchStart = (e: TouchEvent) => {
  if (props.mode !== 'carousel') return
  const touch = e.touches[0]
  touchStartX.value = touch.clientX
  touchDeltaX.value = 0
  stopAutoRotate()
}

const onTouchMove = (e: TouchEvent) => {
  if (props.mode !== 'carousel') return
  const touch = e.touches[0]
  touchDeltaX.value = touch.clientX - touchStartX.value
}

const onTouchEnd = () => {
  if (props.mode !== 'carousel') return
  const threshold = 40
  if (touchDeltaX.value > threshold) prevSlide()
  else if (touchDeltaX.value < -threshold) nextSlide()
  touchDeltaX.value = 0
  startAutoRotate()
}

const nextSlide = () => {
  if (props.mode !== 'carousel') return
  currentIndex.value = (currentIndex.value + 1) % props.devices.length
}

const prevSlide = () => {
  if (props.mode !== 'carousel') return
  currentIndex.value = (currentIndex.value - 1 + props.devices.length) % props.devices.length
}

const getSlideStyle = (index: number) => {
  if (props.mode !== 'carousel') return {}
  
  const offset = (index - currentIndex.value + props.devices.length) % props.devices.length
  const dragOffset = isDragging.value ? dragX.value : 0

  const screenWidth = window.innerWidth
  const isMobile = screenWidth < 640
  const isTabletPortrait = screenWidth >= 640 && screenWidth < 1024

  let sideTransform, sideScale, centerScale

  if (isMobile) {
  sideTransform = '25%'
  sideScale = '0.72'
  centerScale = '1.05'
  } else if (isTabletPortrait) {
  sideTransform = '35%'
  sideScale = '0.78'
  centerScale = '1.08'
  } else {
  sideTransform = '60%'
  sideScale = '0.85'
  centerScale = '1.12'
  }

  let style: any = {
    transform: '',
    zIndex: 0,
    opacity: 0.7,
    filter: 'blur(1px)',
    transition: isDragging.value ? 'none' : 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
  }

  if (offset === 0) {
    style.transform = `translateX(calc(${dragOffset}px)) scale(${centerScale})`
    style.zIndex = 10
    style.opacity = 1
    style.filter = 'none'
    style.pointerEvents = 'auto'
  } else if (offset === 1) {
    style.transform = `translateX(${sideTransform}) scale(${sideScale})`
    style.zIndex = 5
    style.pointerEvents = 'none'
  } else if (offset === props.devices.length - 1) {
    style.transform = `translateX(-${sideTransform}) scale(${sideScale})`
    style.zIndex = 5
    style.pointerEvents = 'none'
  } else {
    style.opacity = 0
    style.transform = 'translateX(0) scale(0.8)'
    style.zIndex = 0
  }

  return style
}

// Slightly increase device size for wide-screen mockups to reduce padding
const getDeviceScale = (type: Device['type']) => {
  const map: Record<Device['type'], number> = {
    phone: 1,
    iphone: 1,
    'iphone-pro': 1,
    android: 1,
    tablet: 1.02,
    laptop: 1.18,
    desktop: 1.22,
    ultrawide: 1.2,
    tv: 1.18,
    watch: 1
  }
  return map[type] ?? 1
}

const rotateWallpaper = () => {
  const wallpapers = dbWallpapers.value.length > 0 ? dbWallpapers.value : (props.wallpapers || [])
  currentWallpaperIndex.value = (currentWallpaperIndex.value + 1) % (wallpapers.length || 1)
  
  // Process each device independently to avoid cross-contamination
  props.devices.forEach(d => {
    const device = getDeviceTypeFromString(d.type as unknown as string)
    
    // Advance device rotation index
    const currentRotationIndex = deviceRotationMap.value.get(d.type) || 0
    const nextRotationIndex = currentRotationIndex + 1
    deviceRotationMap.value.set(d.type, nextRotationIndex)
    
    // Get the NEW wallpaper after rotation
    const newWallpaper = getSelectedWallpaperForDevice(d.type)
    
    if (newWallpaper) {
      // Reset variation rotation for the new wallpaper to start from first variation
      const variationKey = `${(newWallpaper as any).id}-${device}`
      deviceVariationRotationMap.value.set(variationKey, 0)
    }
  })
}

const startAutoRotate = () => {
  if (!props.autoRotate || props.mode !== 'carousel') return

  stopAutoRotate()
  autoRotateInterval = setInterval(nextSlide, props.rotateInterval)
  wallpaperInterval = setInterval(rotateWallpaper, props.wallpaperRotateInterval)
}

const stopAutoRotate = () => {
  if (autoRotateInterval) {
    clearInterval(autoRotateInterval)
    autoRotateInterval = null
  }
  if (wallpaperInterval) {
    clearInterval(wallpaperInterval)
    wallpaperInterval = null
  }
}

const selectDevice = (index: number) => {
  emit('device-change', index)
}

const handleDeviceClick = (device: Device, index: number) => {
  emit('device-click', device, index)
}

// Helper functions for device variation management
const getDeviceVariationCount = (deviceType: Device['type']): number => {
  const wp = getSelectedWallpaperForDevice(deviceType)
  if (!wp) return 1
  
  const device = getDeviceTypeFromString(deviceType as unknown as string)
  const dvl: any[] = (wp as any).deviceVariationsList
  
  if (Array.isArray(dvl) && dvl.length > 0) {
    const entry = dvl.find(v => v.device === device && Array.isArray(v.variations))
    if (entry && entry.variations.length > 0) {
      return entry.variations.length
    }
  }
  
  return 1
}

const getCurrentVariationIndex = (deviceType: Device['type']): number => {
  const wp = getSelectedWallpaperForDevice(deviceType)
  if (!wp) return 0
  
  const device = getDeviceTypeFromString(deviceType as unknown as string)
  const variationKey = `${(wp as any).id}-${device}`
  return deviceVariationRotationMap.value.get(variationKey) || 0
}

const nextVariation = (deviceType: Device['type']) => {
  const wp = getSelectedWallpaperForDevice(deviceType)
  if (!wp) return
  
  const device = getDeviceTypeFromString(deviceType as unknown as string)
  const dvl: any[] = (wp as any).deviceVariationsList
  
  if (Array.isArray(dvl) && dvl.length > 0) {
    const entry = dvl.find(v => v.device === device && Array.isArray(v.variations) && v.variations.length > 1)
    if (entry) {
      const variationKey = `${(wp as any).id}-${device}`
      const currentIndex = deviceVariationRotationMap.value.get(variationKey) || 0
      const nextIndex = (currentIndex + 1) % entry.variations.length
      deviceVariationRotationMap.value.set(variationKey, nextIndex)
    }
  }
}

const prevVariation = (deviceType: Device['type']) => {
  const wp = getSelectedWallpaperForDevice(deviceType)
  if (!wp) return
  
  const device = getDeviceTypeFromString(deviceType as unknown as string)
  const dvl: any[] = (wp as any).deviceVariationsList
  
  if (Array.isArray(dvl) && dvl.length > 0) {
    const entry = dvl.find(v => v.device === device && Array.isArray(v.variations) && v.variations.length > 1)
    if (entry) {
      const variationKey = `${(wp as any).id}-${device}`
      const currentIndex = deviceVariationRotationMap.value.get(variationKey) || 0
      const prevIndex = (currentIndex - 1 + entry.variations.length) % entry.variations.length
      deviceVariationRotationMap.value.set(variationKey, prevIndex)
    }
  }
}

// Watchers
watch(() => props.mode, (newMode) => {
  if (newMode === 'carousel') {
    startAutoRotate()
  } else {
    stopAutoRotate()
  }
})

// Lifecycle
onMounted(() => {
  fetchWallpapers()
  // Realtime updates: subscribe to Firestore so label changes reflect automatically
  const q = query(wallpapersCollection, orderBy('createdAt', 'desc'), limit(120))
  unsubscribeWallpapers = onSnapshot(q, (snapshot) => {
    const list: any[] = []
    snapshot.forEach(doc => {
      const data: any = doc.data()
      list.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date()
      })
    })
    dbWallpapers.value = list as any
  // Trigger recompute of ranked map & reset rotation
  deviceRotationMap.value = new Map()
  }, (err) => {
    console.error('Realtime wallpapers subscription error:', err)
  })
  if (props.mode === 'carousel') {
    startAutoRotate()
  }
  // add touch listeners for swipe
  if (carouselRef.value) {
    carouselRef.value.addEventListener('touchstart', onTouchStart, { passive: true })
    carouselRef.value.addEventListener('touchmove', onTouchMove, { passive: true })
    carouselRef.value.addEventListener('touchend', onTouchEnd, { passive: true })
  }
})

onUnmounted(() => {
  stopAutoRotate()
  if (carouselRef.value) {
    carouselRef.value.removeEventListener('touchstart', onTouchStart)
    carouselRef.value.removeEventListener('touchmove', onTouchMove)
    carouselRef.value.removeEventListener('touchend', onTouchEnd)
  }
  if (unsubscribeWallpapers) {
    unsubscribeWallpapers()
    unsubscribeWallpapers = null
  }
})
</script>

<style scoped>
.device-showcase-container {
  position: relative;
  width: 100%;
}

/* Carousel Styles */
.carousel-container {
  position: relative;
  width: 100%;
  max-width: 1200px;
  height: 400px;
  margin: 1rem auto;
  padding: 0 0.5rem;
  perspective: 1000px;
  overflow: hidden;
}

@media (min-width: 640px) and (max-width: 1023px) {
  .carousel-container {
    padding: 0 1rem;
    overflow: hidden;
    height: 450px;
  }
}

@media (min-width: 1024px) {
  .carousel-container {
    padding: 0 1rem;
    overflow: visible;
    height: 500px;
    margin: 1.5rem auto;
  }
}

.carousel-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.carousel-slide {
  position: absolute;
  width: 85%;
  max-width: 280px;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  will-change: transform, opacity, z-index;
  transform-origin: center center;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
}

@media (min-width: 480px) {
  .carousel-slide {
    width: 75%;
    max-width: 280px;
  }
}

@media (min-width: 640px) {
  .carousel-slide {
    width: 300px;
  }
}

/* Grid Styles */
.grid-container {
  padding: 1rem 0.5rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

@media (min-width: 640px) {
  .grid-container {
    padding: 1.5rem 1rem;
  }
}

.grid-item {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.grid-item:hover {
  transform: translateY(-4px);
}

/* Single Mode Styles */
.single-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem 0.5rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

@media (min-width: 640px) {
  .single-container {
    padding: 1.5rem 1rem;
  }
}

/* Controls */
.carousel-controls {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
  padding: 0 0.5rem;
}

@media (min-width: 640px) {
  .carousel-controls {
    margin-top: 1.5rem;
    padding: 0 1rem;
  }
}

.control-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.device-selector {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0 0.5rem;
}

@media (min-width: 640px) {
  .device-selector {
    margin-top: 1.5rem;
    padding: 0 1rem;
  }
}

.selector-button {
  min-width: 80px;
}

@media (max-width: 640px) {
  .device-selector {
    gap: 0.25rem;
  }
  
  .selector-button {
    min-width: 60px;
    font-size: 0.75rem;
  }
}

/* Variation Controls */
.variation-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding: 0 0.5rem;
}

@media (min-width: 640px) {
  .variation-controls {
    margin-top: 1.5rem;
    padding: 0 1rem;
  }
}

.variation-counter {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  min-width: 60px;
  text-align: center;
}

.variation-indicator {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-left: 0.5rem;
  font-weight: 400;
}

/* Enhanced control buttons for variations */
.variation-controls .control-button {
  width: auto;
  height: 36px;
  border-radius: 6px;
  padding: 0 12px;
  font-size: 0.875rem;
}
</style>